import React, { useRef } from 'react';

import { ASK_AI_SCOPE } from '@lumapps/ask-ai/constants';
import { ASK_AI } from '@lumapps/ask-ai/keys';
import { margin, useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiChevronDown, mdiChevronUp } from '@lumapps/lumx/icons';
import { Alignment, Button, FlexBox, Orientation, Placement, Size } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';

import './index.scss';

const CLASSNAME = 'ask-ai-toggle-button';

export interface AskAiToggleButtonProps {
    onClick: () => void;
    isMoreOpen?: boolean;
    isConversationalEnabled?: boolean;
}

/** Button used to toggle the ask ai visibility */
export const AskAiToggleButton: React.FC<AskAiToggleButtonProps> = ({
    onClick,
    isMoreOpen,
    isConversationalEnabled = false,
}) => {
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(ASK_AI_SCOPE);
    const { block } = useClassnames(CLASSNAME);

    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleClick = () => {
        onClick();
        /**
         * If we close the section, move the button back into view
         * We need this because ask ai may sometimes response with long answers and
         * closing the banner causes the view to remain in the middle of the page.
         */
        if (isMoreOpen) {
            setTimeout(() => {
                buttonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 0);
        }
    };

    return (
        <FlexBox
            orientation={Orientation.horizontal}
            hAlign={Alignment.center}
            vAlign={Alignment.spaceEvenly}
            className={block({ 'with-separators': isConversationalEnabled })}
        >
            <Button
                ref={buttonRef}
                onClick={handleClick}
                emphasis="low"
                rightIcon={isMoreOpen ? mdiChevronUp : mdiChevronDown}
                className={margin(Placement.TOP, Size.huge)}
                {...get({ element: 'button', action: 'show-more' })}
            >
                {isMoreOpen ? translateKey(ASK_AI.COLLAPSE_ANSWER) : translateKey(ASK_AI.EXPAND_ANSWER)}
            </Button>
        </FlexBox>
    );
};
